import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import EducationalDisclaimer from '@/components/feature/EducationalDisclaimer';

const NevadaRealIDPage: React.FC = () => (
  <div className="min-h-screen bg-white">
    <Header />
    <section className="pt-28 pb-12 bg-obsidian-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center space-x-2 text-sm text-obsidian-400 mb-4">
          <Link to="/" className="hover:text-gold-400">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/nevada-dmv-info" className="hover:text-gold-400">DMV Info</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gold-400">REAL ID</span>
        </div>
        <h1 className="font-display text-4xl font-bold text-white mb-3">Nevada REAL ID Requirements</h1>
        <p className="text-obsidian-300">What you need to know about getting a REAL ID-compliant driver&apos;s license or ID card in Nevada.</p>
      </div>
    </section>
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <EducationalDisclaimer />
      <div className="space-y-8 text-obsidian-700">
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">What Is REAL ID?</h2>
          <p>The REAL ID Act established minimum security standards for state-issued driver&apos;s licenses and ID cards. A REAL ID-compliant Nevada license or ID is required to board domestic flights and access certain federal facilities. Nevada REAL ID cards are marked with a gold star in the upper right corner.</p>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Documents Required for REAL ID</h2>
          <div className="space-y-4">
            {[
              { category: 'Proof of Identity (1 document)', items: ['U.S. Passport or Passport Card', 'U.S. Birth Certificate (certified copy)', 'Permanent Resident Card', 'Employment Authorization Document'] },
              { category: 'Proof of Social Security Number (1 document)', items: ['Social Security Card', 'W-2 Form', 'Pay Stub with full SSN', '1099 Form'] },
              { category: 'Proof of Nevada Residency (2 documents)', items: ['Utility bill (within 60 days)', 'Bank statement (within 60 days)', 'Lease or mortgage agreement', 'Nevada voter registration card'] },
            ].map(cat => (
              <div key={cat.category} className="border border-obsidian-100 rounded-lg p-4">
                <p className="font-semibold text-obsidian-900 text-sm mb-2">{cat.category}</p>
                <ul className="space-y-1">
                  {cat.items.map(item => (
                    <li key={item} className="flex items-center space-x-2 text-xs text-obsidian-600">
                      <i className="ri-arrow-right-s-line text-gold-500"></i><span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">REAL ID vs. Standard License</h2>
          <div className="bg-obsidian-50 rounded-xl p-6">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-obsidian-200"><th className="text-left py-2 font-semibold text-obsidian-900">Feature</th><th className="text-center py-2 font-semibold text-obsidian-900">REAL ID</th><th className="text-center py-2 font-semibold text-obsidian-900">Standard</th></tr></thead>
              <tbody className="divide-y divide-obsidian-100">
                {[
                  ['Domestic Air Travel', '✓', '✗'],
                  ['Federal Facilities Access', '✓', '✗'],
                  ['Drive in Nevada', '✓', '✓'],
                  ['Gold Star Marker', '✓', '✗'],
                  ['Additional Documents Required', 'Yes', 'No'],
                ].map(([l, r, s]) => (
                  <tr key={l}><td className="py-2.5 text-obsidian-700">{l}</td><td className="py-2.5 text-center text-green-600 font-medium">{r}</td><td className="py-2.5 text-center text-obsidian-500">{s}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <div className="bg-gold-50 border border-gold-200 rounded-xl p-6 text-center">
          <h3 className="font-display font-bold text-obsidian-900 text-lg mb-2">Questions About REAL ID?</h3>
          <p className="text-obsidian-600 text-sm mb-4">Contact Speedy DMV Services for guidance on Nevada DMV requirements.</p>
          <Link to="/contact" className="inline-block bg-gold-gradient text-obsidian-900 px-6 py-2.5 rounded-lg font-bold text-sm hover:brightness-110 transition-all whitespace-nowrap">Contact Us</Link>
        </div>
      </div>
    </div>
    <Footer />
  </div>
);

export default NevadaRealIDPage;
